<template>
  <div v-if="!loading" class="card">
    <transition name="modal">
      <delete-modal
        v-if="showDeleteModal"
        :loading="loadingDelete"
        @close="closeDeleteModal()"
        @confirmDelete="deleteElement"
      ></delete-modal>
    </transition>

    <transition name="modal">
      <delete-modal
        v-if="imageIdToDelete"
        :loading="loadingDelete"
        @close="imageIdToDelete = null"
        @confirmDelete="deleteImage"
      ></delete-modal>
    </transition>

    <transition name="modal">
      <delete-modal
        v-if="fileIdToDelete"
        :loading="loadingDelete"
        @close="fileIdToDelete = null"
        @confirmDelete="deleteFile"
      ></delete-modal>
    </transition>

    <div class="flex justify-between items-center">
      <p class="font-medium">ID: #{{ checkup.id }}</p>
      <div class="flex items-center">
        <div class="flex space-x-2 mt-2">
          <router-link
            v-if="hasPermission('edit_pets')"
            :to="{
              name: 'petCheckupsEditForm',
              params: {
                id: checkup.id,
                selectedType: pet.breed.pet_type.id,
              },
            }"
          >
            <edit-button />
          </router-link>

          <delete-button
            v-if="hasPermission('delete_pets')"
            class="block delete-icon-button"
            @click="openDeleteModal()"
          />
        </div>
      </div>
    </div>

    <div class="div-line"></div>

    <div
      class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 text-sm mt-6"
    >
      <div>
        <p class="font-medium">Fecha de evaluación</p>
        <p>{{ formatDatetime(checkup.created_at) }}</p>
      </div>

      <div v-if="checkup.created_at !== checkup.updated_at">
        <p class="font-medium">Última actualización</p>
        <p>{{ formatDatetime(checkup.updated_at) }}</p>
      </div>

      <div>
        <p class="font-medium">Peso</p>
        <p>{{ checkup.weight }}kg</p>
      </div>

      <div>
        <p class="font-medium">Composición corporal</p>
        <p>{{ checkup.body_composition.value }}</p>
      </div>

      <div>
        <p class="font-medium">Nivel de actividad</p>
        <p>{{ checkup.activity_level.value }}</p>
      </div>

      <div>
        <p class="font-medium">Actitud al comer</p>
        <p>{{ checkup.eating_attitude.value }}</p>
      </div>

      <div>
        <p class="font-medium">Comida actual</p>
        <p>{{ checkup.food_type.value }}</p>
      </div>

      <div>
        <p class="font-medium">Marca de comida actual</p>
        <p>{{ getValue(checkup.food_brand) }}</p>
      </div>

      <div>
        <p class="font-medium">Come premios, snacks o sobras</p>
        <p>{{ checkup.has_snacks ? "Sí" : "No" }}</p>
      </div>

      <div>
        <p class="font-medium">Alergias alimentarias</p>
        <p
          v-if="
            getAttributes(checkup, 'food_allergy').length === 0 &&
            !checkup.other_food_allergy
          "
        >
          -
        </p>
        <p
          v-for="attribute in getAttributes(checkup, 'food_allergy')"
          :key="attribute.id"
        >
          - {{ attribute.value }}
        </p>
        <p v-if="checkup.other_food_allergy">
          - {{ checkup.other_food_allergy }}
        </p>
      </div>

      <div>
        <p class="font-medium">Problemas de salud</p>
        <p
          v-if="
            getAttributes(checkup, 'disease').length === 0 &&
            !checkup.other_disease
          "
        >
          -
        </p>
        <p
          v-for="attribute in getAttributes(checkup, 'disease')"
          :key="attribute.id"
        >
          - {{ attribute.value }}
        </p>
        <p v-if="checkup.other_disease">- {{ checkup.other_disease }}</p>
      </div>
    </div>

    <div class="mt-2 text-sm">
      <p class="font-medium">Comentarios adicionales</p>
      <p>{{ getValue(checkup.additional_comments) }}</p>
    </div>

    <div class="mt-2 text-sm">
      <div class="flex items-center gap-1">
        <p class="font-medium">Comentarios del especialista</p>
        <!-- <tooltip message="Este campo únicamente es visible para los administradores y sucursales" position="bottom" /> -->
      </div>

      <p>{{ getValue(checkup.specialist_comments) }}</p>
    </div>

    <div class="text-sm mt-2">
      <p class="font-medium">Exámenes de alérgenos</p>

      <div v-if="!allergiesTestFile">
        <input
          class="hidden"
          type="file"
          name="file"
          id="allergy_test"
          ref="file"
          accept="application/pdf"
          @change="handleAllergiesTestFile"
        />

        <label for="allergy_test" class="secondary-button font-normal"
          >+ Agregar Archivo PDF</label
        >
      </div>

      <div v-else class="flex flex-col">
        <p class="text-sm">{{ allergiesTestFileName }}</p>

        <div class="flex space-x-4 mt-2">
          <button
            class="delete-button"
            @click.prevent="
              allergiesTestFile = null;
              allergiesTestFileName = null;
            "
          >
            Remover
          </button>
          <button
            class="secondary-button"
            @click.prevent="createPetCheckupFile('allergy')"
          >
            Guardar
          </button>
        </div>
      </div>

      <div
        class="mt-1 flex space-x-4 items-center"
        v-for="file in checkup.allergy_test_files"
        :key="file.id"
      >
        <button
          class="system-button underline"
          @click.prevent="downloadPdf(file.url)"
        >
          {{ file.name }}
        </button>

        <delete-button
          class="delete-icon-button"
          @click="fileIdToDelete = file.id"
        />
      </div>
    </div>

    <div class="text-sm mt-2">
      <p class="font-medium">Exámenes de salud</p>
      <div v-if="!diseasesTestFile">
        <input
          class="hidden"
          type="file"
          name="file"
          id="disease_test"
          ref="file"
          accept="application/pdf"
          @change="handleDiseasesTestFile"
        />

        <label for="disease_test" class="secondary-button font-normal"
          >+ Agregar Archivo PDF</label
        >
      </div>

      <div v-else class="flex flex-col">
        <p class="text-sm">{{ diseasesTestFileName }}</p>

        <div class="flex space-x-4 mt-2">
          <button
            class="delete-button"
            @click.prevent="
              diseasesTestFile = null;
              diseasesTestFileName = null;
            "
          >
            Remover
          </button>
          <button
            class="secondary-button"
            @click.prevent="createPetCheckupFile('disease')"
          >
            Guardar
          </button>
        </div>
      </div>

      <div
        class="mt-1 flex space-x-4 items-center"
        v-for="file in checkup.disease_test_files"
        :key="file.id"
      >
        <button
          class="system-button underline"
          @click.prevent="downloadPdf(file.url)"
        >
          {{ file.name }}
        </button>

        <delete-button
          class="delete-icon-button"
          @click="fileIdToDelete = file.id"
        />
      </div>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-6">
      <div v-if="checkup.front_image" class="flex flex-col">
        <p class="mx-auto text-sm font-semibold">Imagen frontal</p>

        <div
          class="aspect-square w-full border border-primary-500 rounded-lg overflow-hidden mx-auto relative"
        >
          <img
            class="w-full h-full object-contain object-center"
            :src="checkup.front_image ? checkup.front_image.url : defaultImage"
            loading="lazy"
          />

          <delete-button
            class="delete-icon-button absolute top-0 right-0"
            @click="imageIdToDelete = checkup.front_image.id"
          />
        </div>
      </div>

      <div v-else-if="!frontImage" class="mt-6 mx-auto">
        <input
          class="hidden"
          type="file"
          name="image"
          id="frontImage"
          ref="image"
          accept="image/*"
          @change="handleFrontImage"
        />

        <label for="frontImage" class="secondary-button font-normal"
          >+ Agregar Imagen Frontal</label
        >
      </div>

      <div v-else class="mt-6 flex flex-col mx-auto">
        <p class="text-sm font-semibold mx-auto">Imagen Frontal</p>

        <img
          ref="imagePreview"
          class="w-full h-full sm:w-72 sm:h-72 rounded-lg object-contain object-center border border-primary-500"
          :src="frontImagePreview"
        />

        <div class="flex justify-evenly w-full mt-2">
          <button
            class="delete-button"
            @click.prevent="
              frontImage = null;
              frontImagePreview = null;
            "
          >
            Remover
          </button>
          <button
            class="secondary-button"
            @click.prevent="createPetCheckupImage('front')"
          >
            Guardar
          </button>
        </div>
      </div>

      <div v-if="checkup.side_image" class="flex flex-col">
        <p class="mx-auto text-sm font-semibold">Imagen de Lado</p>
        <div
          class="aspect-square w-full border border-primary-500 rounded-lg overflow-hidden mx-auto relative"
        >
          <img
            class="w-full h-full object-contain object-center"
            :src="checkup.side_image ? checkup.side_image.url : defaultImage"
            loading="lazy"
          />

          <delete-button
            class="delete-icon-button absolute top-0 right-0"
            @click="imageIdToDelete = checkup.side_image.id"
          />
        </div>
      </div>

      <div v-else-if="!sideImage" class="mt-6 mx-auto">
        <input
          class="hidden"
          type="file"
          name="image"
          id="sideImage"
          ref="image"
          accept="image/*"
          @change="handleSideImage"
        />

        <label for="sideImage" class="secondary-button font-normal"
          >+ Agregar Imagen de Lado</label
        >
      </div>

      <div v-else class="mt-6 flex flex-col mx-auto">
        <p class="text-sm font-semibold mx-auto">Imagen de Lado</p>

        <img
          ref="imagePreview"
          class="w-full h-full sm:w-72 sm:h-72 rounded-lg object-contain object-center border border-primary-500"
          :src="sideImagePreview"
        />

        <div class="flex justify-evenly w-full mt-2">
          <button
            class="delete-button"
            @click.prevent="
              sideImage = null;
              sideImagePreview = null;
            "
          >
            Remover
          </button>
          <button
            class="secondary-button"
            @click.prevent="createPetCheckupImage('side')"
          >
            Guardar
          </button>
        </div>
      </div>

      <div v-if="checkup.top_image" class="flex flex-col">
        <p class="mx-auto text-sm font-semibold">Imagen Superior</p>
        <div
          class="aspect-square w-full border border-primary-500 rounded-lg overflow-hidden mx-auto relative"
        >
          <img
            class="w-full h-full object-contain object-center"
            :src="checkup.top_image ? checkup.top_image.url : defaultImage"
            loading="lazy"
          />

          <delete-button
            class="delete-icon-button absolute top-0 right-0"
            @click="imageIdToDelete = checkup.top_image.id"
          />
        </div>
      </div>

      <div v-else-if="!topImage" class="mt-6 mx-auto">
        <input
          class="hidden"
          type="file"
          name="image"
          id="topImage"
          ref="image"
          accept="image/*"
          @change="handleTopImage"
        />

        <label for="topImage" class="secondary-button font-normal"
          >+ Agregar Imagen Superior</label
        >
      </div>

      <div v-else class="mt-6 flex flex-col mx-auto">
        <p class="text-sm font-semibold mx-auto">Imagen Superior</p>

        <img
          ref="imagePreview"
          class="w-full h-full sm:w-72 sm:h-72 rounded-lg object-contain object-center border border-primary-500"
          :src="topImagePreview"
        />

        <div class="flex justify-evenly w-full mt-2">
          <button
            class="delete-button"
            @click.prevent="
              topImage = null;
              topImagePreview = null;
            "
          >
            Remover
          </button>
          <button
            class="secondary-button"
            @click.prevent="createPetCheckupImage('top')"
          >
            Guardar
          </button>
        </div>
      </div>
    </div>
  </div>

  <div v-else class="flex w-full h-full items-center">
    <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
  </div>
</template>

<script>
import api from "@/api";
import deleteMixin from "@/mixins/deleteMixin";
import { formatDatetime } from "@/utils/dateFormatter";
export default {
  name: "Evaluation",

  data() {
    return {
      frontImage: null,
      frontImagePreview: null,
      sideImage: null,
      sideImagePreview: null,
      topImage: null,
      topImagePreview: null,
      loading: false,
      loadingDelete: false,
      imageIdToDelete: null,
      fileIdToDelete: null,
      allergiesTestFile: null,
      allergiesTestFileName: null,
      diseasesTestFile: null,
      diseasesTestFileName: null,
    };
  },

  props: {
    checkup: Object,
    pet: Object,
  },

  mixins: [deleteMixin],

  methods: {
    formatDatetime,

    getAttributes(checkup, type) {
      return checkup.pet_checkup_attributes.filter((att) => att.type === type);
    },

    getValue(field) {
      if (!field) {
        return "-";
      }

      return field;
    },

    downloadPdf(url) {
      const link = document.createElement("a");
      link.href = url;
      link.download = url.substring(url.lastIndexOf("/") + 1);
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    handleFrontImage(e) {
      let image = e.target.files[0];

      if (image && image.size > 25 * 1024 * 1024) {
        this.$store.dispatch(
          "errorSnack",
          "La imagen excede el tamaño máximo de 25mb."
        );
        return;
      }

      if (image) {
        let url = URL.createObjectURL(image);
        let img = new Image();

        img.onload = () => {
          URL.revokeObjectURL(url);
          let reader = new FileReader();
          reader.onload = (e) => {
            this.frontImage = image;
            this.frontImagePreview = e.target.result;
          };
          reader.readAsDataURL(image);
        };

        img.onerror = () => {
          URL.revokeObjectURL(url);
          alert("Unable to load image");
        };

        img.src = url;
      }
    },

    handleSideImage(e) {
      let image = e.target.files[0];

      if (image && image.size > 25 * 1024 * 1024) {
        this.$store.dispatch(
          "errorSnack",
          "La imagen excede el tamaño máximo de 25mb."
        );
        return;
      }

      if (image) {
        let url = URL.createObjectURL(image);
        let img = new Image();

        img.onload = () => {
          URL.revokeObjectURL(url);
          let reader = new FileReader();
          reader.onload = (e) => {
            this.sideImage = image;
            this.sideImagePreview = e.target.result;
          };
          reader.readAsDataURL(image);
        };

        img.onerror = () => {
          URL.revokeObjectURL(url);
          alert("Unable to load image");
        };

        img.src = url;
      }
    },

    handleTopImage(e) {
      let image = e.target.files[0];

      if (image && image.size > 25 * 1024 * 1024) {
        this.$store.dispatch(
          "errorSnack",
          "La imagen excede el tamaño máximo de 25mb."
        );
        return;
      }

      if (image) {
        let url = URL.createObjectURL(image);
        let img = new Image();

        img.onload = () => {
          URL.revokeObjectURL(url);
          let reader = new FileReader();
          reader.onload = (e) => {
            this.topImage = image;
            this.topImagePreview = e.target.result;
          };
          reader.readAsDataURL(image);
        };

        img.onerror = () => {
          URL.revokeObjectURL(url);
          alert("Unable to load image");
        };

        img.src = url;
      }
    },

    handleAllergiesTestFile(e) {
      const file = e.target.files[0];

      if (file && file.size > 25 * 1024 * 1024) {
        this.$store;
        e.target.value = "";
        return;
      }

      if (file) {
        const url = URL.createObjectURL(file);

        if (file.type === "application/pdf") {
          const reader = new FileReader();
          reader.onload = (e) => {
            this.allergiesTestFile = file;
            this.allergiesTestFileName = file.name;
          };
          reader.readAsArrayBuffer(file);
        } else {
          console.error("Unsupported file type:", file.type);
        }
      }
    },

    handleDiseasesTestFile(e) {
      const file = e.target.files[0];

      if (file && file.size > 25 * 1024 * 1024) {
        this.$store;
        e.target.value = "";
        return;
      }

      if (file) {
        const url = URL.createObjectURL(file);

        if (file.type === "application/pdf") {
          const reader = new FileReader();
          reader.onload = (e) => {
            this.diseasesTestFile = file;
            this.diseasesTestFileName = file.name;
          };
          reader.readAsArrayBuffer(file);
        } else {
          console.error("Unsupported file type:", file.type);
        }
      }
    },

    async createPetCheckupImage(type) {
      this.loading = true;

      const formData = new FormData();

      switch (type) {
        case "front":
          formData.append("frontImage", this.frontImage);
          break;
        case "side":
          formData.append("sideImage", this.sideImage);
          break;
        case "top":
          formData.append("topImage", this.topImage);
          break;
        default:
          break;
      }

      try {
        const response = await api.pets.createCheckupImage(
          this.checkup.id,
          formData
        );
        this.$store.dispatch("successSnack", response.message);
        this.frontImage = null;
        this.frontImagePreview = null;
        this.sideImage = null;
        this.sideImagePreview = null;
        this.topImage = null;
        this.topImagePreview = null;
        this.$emit("createdImage");
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    async deleteImage() {
      this.loadingDelete = true;

      try {
        const response = await api.pets.deleteCheckupImage(
          this.imageIdToDelete
        );
        this.$store.dispatch("successSnack", response.message);
        this.imageIdToDelete = null;
        this.$emit("deletedImage");
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingDelete = false;
    },

    async createPetCheckupFile(type) {
      this.loading = true;

      const formData = new FormData();

      switch (type) {
        case "allergy":
          formData.append("allergiesTestFile", this.allergiesTestFile);
          break;
        case "disease":
          formData.append("diseasesTestFile", this.diseasesTestFile);
          break;
        default:
          break;
      }

      try {
        const response = await api.pets.createCheckupFile(
          this.checkup.id,
          formData
        );
        this.$store.dispatch("successSnack", response.message);
        this.allergiesTestFile = null;
        this.allergiesTestFileName = null;
        this.$emit("createdFile");
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    async deleteFile() {
      this.loadingDelete = true;

      try {
        const response = await api.pets.deleteCheckupFile(this.fileIdToDelete);
        this.$store.dispatch("successSnack", response.message);
        this.fileIdToDelete = null;
        this.$emit("deletedImage");
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingDelete = false;
    },

    async deleteElement() {
      this.loadingDelete = true;

      try {
        const response = await api.pets.deleteCheckup(this.checkup.id);
        this.$store.dispatch("successSnack", response.message);
        this.closeDeleteModal();
        this.$emit("deletedCheckup");
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingDelete = false;
    },
  },
};
</script>